import { Suspense, lazy, type ReactNode } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "./components/Loader";
import SearchBusiness from "./pages/Search_Business";
import ConnectGoogle from "./pages/ConnectGoogle";
import OAuthCallback from "./pages/OAuthCallback";
import KYBVerification from "./pages/KYBVerification";
import KYBEmailConfirm from "./pages/KYBEmailConfirm";
import SubscriptionCallback from "./pages/SubscriptionCallback";
import PulseCheck from "./pages/Pulse_Check";
import ImportSync from "./pages/Import_Sync";
import DashboardLayout from "./dashboard/layout/DashboardLayout";
import { tokenService } from "./services/tokenService";

// ── Auth guard ────────────────────────────────────────────────────────────────
// Checks for a stored access token BEFORE rendering any dashboard page.
// If none exists the user is bounced to /login instantly — no API call,
// no loading flash.
function RequireAuth({ children }: { children: ReactNode }) {
  const hasToken = Boolean(tokenService.getAccessToken());
  if (!hasToken) {
    try {
      localStorage.setItem(
        "toronq:flash",
        JSON.stringify({ type: "error", message: "Your session has expired. Please sign in again." }),
      );
    } catch { /* ignore */ }
    return <Navigate to="/login" replace />;
  }
  return children;
}

const SplashScreen = lazy(() => import("./pages/SplashScreen"));
const Login = lazy(() => import("./pages/Login"));
const Verify2FA = lazy(() => import("./pages/Verify_2FA"));
const VerifyEmail = lazy(() => import("./pages/Verify_Email"));
const Register = lazy(() => import("./pages/Register"));
const ForgottenPassword = lazy(() => import("./pages/Forgotten_Password"));
const ResetPassword = lazy(() => import("./pages/Reset_Password"));
const OnBoarding = lazy(() => import("./pages/On_Boarding"));
const NoPageFound = lazy(() => import("./pages/No_Page_Found"));

const Overview = lazy(() => import("./dashboard/pages/Overview"));
const Reviews = lazy(() => import("./dashboard/pages/Reviews"));
const Locations = lazy(() => import("./dashboard/pages/Locations"));
const Grow = lazy(() => import("./dashboard/pages/Grow"));
const ReputationScore = lazy(() => import("./dashboard/pages/ReputationScore"));
const CrisisAlerts = lazy(() => import("./dashboard/pages/CrisisAlerts"));
const ReviewSocial = lazy(() => import("./dashboard/pages/ReviewSocial"));
const AeoInsights = lazy(() => import("./dashboard/pages/AeoInsights"));
const Competitors = lazy(() => import("./dashboard/pages/Competitors"));
const Referral = lazy(() => import("./dashboard/pages/Referral"));
const Settings = lazy(() => import("./dashboard/pages/Settings"));
const Subscription = lazy(() => import("./dashboard/pages/Subscription"));
const Notifications = lazy(() => import("./dashboard/pages/Notifications"));
const HelpCenter = lazy(() => import("./dashboard/pages/HelpCenter"));
const ContactSupport = lazy(() => import("./dashboard/pages/ContactSupport"));
const LegalDocument = lazy(() => import("./dashboard/pages/LegalDocument"));

function App() {
  return (
    <>
      <Suspense fallback={<Loader text="Loading ToronQ AI..." />}>
        <Routes>
          <Route path="/" element={<SplashScreen />} />

          <Route path="/login" element={<Login />} />
          <Route path="/verify-2fa" element={<Verify2FA />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgottenPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/onboarding" element={<OnBoarding />} />
          <Route path="/search-business" element={<SearchBusiness />} />
          <Route path="/connect-google" element={<ConnectGoogle />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route path="/kyb-verification" element={<KYBVerification />} />
          {/* Email link from backend lands here — simple confirmation page, no auth needed */}
          <Route path="/kyb-verification/:tokenParam" element={<KYBEmailConfirm />} />
          <Route path="/subscription/callback" element={<SubscriptionCallback />} />
          <Route path="/pulse-check" element={<PulseCheck />} />
          <Route path="/import-sync" element={<ImportSync />} />

          <Route path="/dashboard" element={<RequireAuth><DashboardLayout /></RequireAuth>}>
            <Route index element={<Navigate to="overview" replace />} />

            <Route path="overview" element={<Overview />} />
            <Route path="reviews" element={<Reviews />} />
            <Route path="locations" element={<Locations />} />
            <Route path="grow" element={<Grow />} />
            <Route path="reputation-score" element={<ReputationScore />} />
            <Route path="crisis-alerts" element={<CrisisAlerts />} />
            <Route path="review-social" element={<ReviewSocial />} />
            <Route path="aeo-insights" element={<AeoInsights />} />
            <Route path="competitors" element={<Competitors />} />
            <Route path="referral" element={<Referral />} />
            <Route path="settings" element={<Settings />} />
            <Route path="subscription" element={<Subscription />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="help-center" element={<HelpCenter />} />
            <Route path="contact-support" element={<ContactSupport />} />
            <Route path="legal/:docType" element={<LegalDocument />} />
          </Route>

          <Route path="*" element={<NoPageFound />} />
        </Routes>
      </Suspense>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </>
  );
}

export default App;
